import {
  BgImage,
  Carousel,
  ContactCTA,
  Header,
  Intro,
  KitchenExploreCTA,
  PartKeyTable,
  PartList,
  SustainabilityCTA,
} from "@prmf/site-components";
import { CollectionGroup, CollectionItem } from "@prmf/site-data";
import { useLoaderData, useParams } from "@remix-run/react";

const pages = {
  handles: {
    header:
      "Handles provide a simple way of transforming a kitchen. Our extensive range allows customers to choose from a variety of styles: sleek and hidden, traditional and classic, or even statement and showstopper.",
    intro:
      "Each door range offers a selection of handles that can change the look and feel of your kitchen.",
    divider:
      "Choose from bar, cup and knob, or edge-pull in a variety of sizes, finishes and styles",
  },
  sinks: {
    header:
      "Paula Rosa Manhattan works closely with hand-picked sink suppliers to offer a variety of design solutions at different price points. Choose from our selection of stainless steel sinks for the classic kitchen design.",
    intro:
      "Upgrade your surface space with a solid worktop and an undermount sink for a luxurious finish to a showstopper kitchen.",
    partners: [
      "/media/assets/partners-carbon.png",
      "/media/assets/partners-franke.png",
      "/media/assets/partners-leisure.png",
      "/media/assets/partners-reginox.png",
      "/media/assets/partners-rangemaster.png",
      "/media/assets/partners-1810.png",
    ],
  },
  cabinets: {
    header:
      "A cabinet is the frame of your kitchen. It provides space for all your kitchen storage needs. By choosing matching colours or making a statement with a contrasting colour, this frame can blend in with the rest of the kitchen.",
    intro:
      "Platinum White comes as standard with our Denver range doors. All other ranges have a choice of five different cabinet colours, allowing customers to further personalise their kitchens.",
  },
  taps: {
    header:
      "Choosing a tap isn’t just a question of colour and style, but also practicality and functionality in your kitchen space. Whether you want a simple, single lever tap, an industrial-style, pull-down spray or a handy hot water tap, we can help create the right look for your sink area.",
    intro:
      "Aesthetically, we also have a large range of different colours, including stainless steel, black and copper. We also offer different styles, such as knurled, traditional, and swivel.",
    divider:
      "We offer different styles, such as knurled, traditional, and swivel.",
    partners: [
      "/media/assets/partners-carbon.png",
      "/media/assets/partners-franke.png",
      "/media/assets/partners-leisure.png",
      "/media/assets/partners-reginox.png",
      "/media/assets/partners-rangemaster.png",
      "/media/assets/partners-1810.png",
    ],
  },
  shelving: {
    header:
      "Adding shelving to your kitchen is a cost-effective, simple way of mixing up the look and feel of the space. Choosing a contrasting carcass colour to suit the rest of the kitchen is an easy way of making an eye-catching feature. Shelving allows a space that may seem enclosed feel more open and airy, whilst maintaining functionality.",
    intro:
      "Whether you want to show off your dazzling array of glasses, or some stylish decorative items, open shelving is the perfect, on-trend solution.",
  },
  laminates: {
    header:
      "A laminate worktop offers a resilient surface that withstands daily wear whilst maintaining a sleek appearance. Paula Rosa Manhattan uses only the very best, premium materials for worktops.\nExperience a spectrum of design possibilities, with worktops available in a range of colours, sizes, and styles. Two edge profiles are available: the classic post formed rounded edge, or the more modern, on-trend square edge. Customise your worktop space with upstands and laminate splashbacks. Worktops are available in depths between 600mm and 900mm.",
    intro:
      "Laminate worktops allow for customers to bring the look of a solid stone or wood surface into their kitchen at a fraction of the price.",
  },
  splashbacks: {
    header:
      "Infuse your kitchen with modern elegance: stainless steel and glass splashbacks effortlessly combine functionality with a reflective brilliance that enhances natural light.\nEngineered for both style and substance, splashbacks provide a hygienic, easy-to-clean surface that withstands the demands of a busy kitchen.\nPaula Rosa Manhattan offers a variety of coloured, glass splashbacks in three finishes – gloss, satin and metallic, perfect for any kitchen style. These can be fitted just behind the hob area or make to measure for your own kitchen space.",
    intro:
      "Stainless steel splashbacks are also available in a variety of heights and widths, perfect for any hood and hob combination.",
    divider:
      "Stainless steel splashbacks are available in a selection of sizes to fit perfectly with any hob and hood combination.",
  },
  worktops: {
    header:
      "Crafted from premium materials, solid worktops provide durable and elegant surfaces that stand the test of time, bringing the warmth and character of natural materials into our kitchen. We work with our solid partners to offer a variety of colours, textures and styles.\nCustomise your worktop space with upstands and solid splashbacks. Elevate your kitchen space by adding waterfall edges at the end of worktop runs. Solid worktops are available in depths between 600mm and 900mm.",
    intro:
      "We work closely with our suppliers to offer a variety of styles and finishes across five different price points.",
    partners: [
      "/media/assets/partners-caesarstone.png",
      "/media/assets/partners-dekton.png",
      "/media/assets/partners-silestone.png",
    ],
    divider:
      "A matching upstand or solid splashback adds a finishing touch to your worktop space.",
    dividerDetail:
      "Choose from a selection of edging profiles, such as chamfer, bullnose, and shark nose, to subtly enhance the look and feel of your kitchen",
  },
};

function Divider(props: { title: string; text?: string; id: string }) {
  return (
    <section className="bg-black text-sm text-white">
      <div className="grid gap-10 md:container md:grid-cols-2">
        <div className="space-y-4 place-self-center pt-10 text-center max-md:container md:py-20">
          <h4 className="mx-auto max-w-sm text-2xl font-light">
            {props.title}
          </h4>
          {props.text && (
            <p className="mx-auto max-w-sm font-light">{props.text}</p>
          )}
        </div>
        <div className="relative">
          <BgImage
            className="aspect-[4/3.5]"
            src={`/media/assets/parts-${props.id}-divider.jpg`}
          />
        </div>
      </div>
    </section>
  );
}

export default function KitchenParts() {
  const { items } = useLoaderData<{
    items: CollectionItem[] | CollectionGroup[];
  }>();
  const slug = useParams<{ slug: keyof typeof pages }>().slug!;
  const data = pages[slug] || {};
  const hasKeyTable = ["handles", "cabinets"].includes(slug);
  const hasDivider = "divider" in data;
  const hasPartnerList = "partners" in data;
  const midIndex = Math.floor(items.length / 2);
  const part1 = hasDivider ? items.slice(0, midIndex) : items;
  const part2 = hasDivider ? items.slice(midIndex) : [];
  return (
    <div>
      <Header bgColor="black">
        {data.header?.split("\n").map((p, i) => <p key={i}>{p}</p>)}
      </Header>
      <Intro title={data.intro} bgColor="black" />
      {hasKeyTable && (
        <div className="container">
          <PartKeyTable />
        </div>
      )}
      {hasPartnerList && (
        <div className="flex flex-col items-center gap-8 bg-[#EFEFEF] p-10">
          <h3 className="text-xl font-semibold">
            We work with some of the best names in <span>{slug}</span>
          </h3>
          <Carousel
            className="container flex items-center justify-center gap-8 text-[#EFEFEF]"
            pagination={false}
          >
            {data.partners.map((p) => (
              <BgImage
                key={p}
                className="mx-auto aspect-video w-[160px] bg-transparent bg-contain"
                src={p}
              />
            ))}
          </Carousel>
        </div>
      )}
      <PartList items={part1} classList="container my-10" />
      {hasDivider && (
        <Divider
          id={slug}
          title={data.divider}
          text={"dividerDetail" in data ? data.dividerDetail : undefined}
        />
      )}
      <PartList items={part2} classList="container my-10" />
      <KitchenExploreCTA />
      <SustainabilityCTA />
      <ContactCTA />
    </div>
  );
}
